import { Modal, Row, Col, Input, Typography, Button } from "antd";
import React, { useState, useEffect } from "react";
import {
  MailOutlined,
  LockOutlined,
  UserOutlined,
  SafetyOutlined,
} from "@ant-design/icons";

const { Title, Text, Paragraph } = Typography;

const OTPInput = ({ value, onChange, numInputs = 6 }) => {
  const [otp, setOtp] = useState(new Array(numInputs).fill(""));
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const inputRefs = React.useRef([]);

  useEffect(() => {
    onChange(otp.join(""));
  }, [otp, onChange]);

  // Auto-focus first empty input on mount
  useEffect(() => {
    const firstEmptyIndex = otp.findIndex(digit => digit === "");
    if (firstEmptyIndex !== -1 && inputRefs.current[firstEmptyIndex]) {
      inputRefs.current[firstEmptyIndex].focus();
    }
  }, []);

  const handleChange = (element, index) => {
    const value = element.value;
    if (value && isNaN(value)) return false;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input if current input is filled
    if (value && index < numInputs - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const newOtp = [...otp];
      
      if (otp[index]) {
        // Clear current input if it has value
        newOtp[index] = "";
        setOtp(newOtp);
      } else if (index > 0) {
        // Move to previous input and clear it if current is empty
        newOtp[index - 1] = "";
        setOtp(newOtp);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < numInputs - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Handle paste functionality
  const handlePaste = (e, index) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").replace(/\D/g, ""); // Remove non-digits
    
    if (pasteData.length > 0) {
      const newOtp = [...otp];
      const availableSlots = numInputs - index;
      const digitsToPaste = pasteData.slice(0, availableSlots).split("");
      
      digitsToPaste.forEach((digit, i) => {
        if (index + i < numInputs) {
          newOtp[index + i] = digit;
        }
      });
      
      setOtp(newOtp);
      
      // Focus the next empty input or the last input
      const nextFocusIndex = Math.min(index + digitsToPaste.length, numInputs - 1);
      inputRefs.current[nextFocusIndex]?.focus();
    }
  };

  const getInputStyle = (index) => ({
    width: "50px",
    height: "50px",
    textAlign: "center",
    fontSize: "20px",
    fontWeight: "600",
    border: `2px solid ${
      focusedIndex === index 
        ? "#3366ff" 
        : otp[index] 
        ? "#52c41a" 
        : "#d9d9d9"
    }`,
    borderRadius: "12px",
    transition: "all 0.2s ease",
    backgroundColor: otp[index] ? "#f6ffed" : "#ffffff",
    boxShadow: focusedIndex === index 
      ? "0 0 0 3px rgba(51, 102, 255, 0.1)" 
      : "none",
    "&:hover": {
      borderColor: "#3366ff",
    },
  });

  return (
    <div style={{ display: "flex", gap: "12px", justifyContent: "center" }}>
      {otp.map((data, index) => (
        <Input
          key={index}
          ref={(ref) => (inputRefs.current[index] = ref)}
          type="text"
          maxLength="1"
          style={getInputStyle(index)}
          value={data}
          onChange={(e) => handleChange(e.target, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={(e) => handlePaste(e, index)}
          onFocus={(e) => {
            e.target.select();
            setFocusedIndex(index);
          }}
          onBlur={() => setFocusedIndex(-1)}
          placeholder="•"
        />
      ))}
    </div>
  );
};

const AuthenticatorCode = ({ 
  visible, 
  setVisible, 
  onVerify, 
  loading = false,
  onTryOtherOptions,
  oTPSentToEmail,
}) => {
  const [otpCode, setOtpCode] = useState("");

  const handleOtpChange = (value) => {
    setOtpCode(value);
  };

  const handleVerify = () => {
    if (otpCode.length === 6 && onVerify) {
      onVerify(otpCode);
    }
  };

  const handleCancel = () => {
    setVisible(false);
    setOtpCode(""); 
  };

  // Clear OTP when modal opens
  useEffect(() => {
    if (visible) {
      setOtpCode("");
    }
  }, [visible]);

  return (
    <Modal
      title={null}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width={520}
      closable={false}
      centered
      keyClose={true}
      styles={{
        body: { padding: "32px 24px" }
      }}
    >
      <div>
        <Row gutter={[16, 32]} justify="center">
          <Col span={24}>
            <div style={{ textAlign: "center", marginBottom: "8px" }}>
              <div
                style={{
                  width: "80px",
                  height: "80px",
                  background:
                    "linear-gradient(135deg, #3366ff 0%, #6600ff 100%)",
                  borderRadius: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 24px",
                  boxShadow: "0 8px 32px rgba(51, 102, 255, 0.3)",
                }}
              >
                {oTPSentToEmail ? (
                  <MailOutlined style={{ fontSize: "36px", color: "white" }} />
                ) : (
                  <SafetyOutlined style={{ fontSize: "36px", color: "white" }} />
                )}
              </div>
              <Title
                level={3}
                style={{ 
                  marginBottom: "8px", 
                  color: "#1a1a1a",
                  fontWeight: "600"
                }}
              >
                {oTPSentToEmail ? "Check Your Email" : "Enter Verification Code"}
              </Title>
              <Text
                style={{
                  color: "#666666",
                  fontSize: "15px",
                  lineHeight: "1.5",
                }}
              >
                {oTPSentToEmail 
                  ? "Enter the 6-digit code sent to your email address"
                  : "Enter the 6-digit code from your authenticator app"
                }
              </Text>
            </div>
          </Col>

          <Col span={24}>
            <div style={{ textAlign: "center" }}>
              <OTPInput
                value={otpCode}
                onChange={handleOtpChange}   
                numInputs={6}
              />
              <div style={{ 
                marginTop: "12px", 
                fontSize: "13px", 
                color: "#999999" 
              }}>
                {oTPSentToEmail 
                  ? "Copy the code from your email and paste it here"
                  : "You can paste your code here"
                }
              </div>
            </div>
          </Col>

          {onTryOtherOptions && !oTPSentToEmail && (
            <Col span={24}>
              <div style={{ textAlign: "center" }}>
                <Button
                  type="link"
                  onClick={onTryOtherOptions}
                  style={{
                    color: "#3366ff",
                    padding: "8px 16px",
                    height: "auto",
                    fontSize: "14px",
                    fontWeight: "500",
                  }}
                >
                  Try other verification methods
                </Button>
              </div>
            </Col>
          )}

          <Col span={24}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                gap: "16px",
              }}
            >
              <Button
                size="large"
                onClick={handleCancel}
                style={{
                  minWidth: "140px",
                  height: "48px",
                  borderRadius: "12px",
                  fontWeight: "500",
                  border: "2px solid #d9d9d9",
                }}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                size="large"
                loading={loading}
                onClick={handleVerify}
                disabled={otpCode.length !== 6}
                style={{
                  minWidth: "140px",
                  height: "48px",
                  color: '#fff',
                  backgroundImage:
                    "linear-gradient(135deg, #3366ff 0%, #6600ff 100%)",
                  border: "none",
                  borderRadius: "12px",
                  fontWeight: "700",
                  boxShadow: otpCode.length === 6 
                    ? "0 4px 16px rgba(51, 102, 255, 0.4)" 
                    : "none",
                  transform: otpCode.length === 6 ? "translateY(-1px)" : "none",
                  transition: "all 0.2s ease",
                }}
              >
                {loading ? "Verifying..." : "Verify & Continue"}
              </Button>
            </div>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default AuthenticatorCode;